import * as M from "./movement.mjs";
import { axialToWorld } from "./hex-layout.mjs";

// Seeded "Frontline" map generator for large battles. Pure: same seed, same map.
// The map is an odd-row-offset rectangle with an even row count, which makes axial point reflection
// h -> (Q - q, rows - 1 - r) an exact symmetry of the board. Every feature is written through set(),
// which writes a hex and its mirror, so both sides always get the same ground.

export const FRONTLINE = { cols: 36, rows: 24, apPerTurn: 12, maxTurns: 30, credits: 2500 };

export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function valueNoise(seed) {
  const hash = (x, y) => {
    let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 2147483647)) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
  const smooth = (t) => t * t * (3 - 2 * t);
  const at = (x, y) => {
    const x0 = Math.floor(x), y0 = Math.floor(y), fx = smooth(x - x0), fy = smooth(y - y0);
    const a = hash(x0, y0), b = hash(x0 + 1, y0), c = hash(x0, y0 + 1), d = hash(x0 + 1, y0 + 1);
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
  return (x, y) => {
    let sum = 0, amp = 1, norm = 0, f = 1;
    for (let o = 0; o < 4; o++) { sum += at(x * f, y * f) * amp; norm += amp; amp *= 0.5; f *= 2; }
    return sum / norm;
  };
}

const quantile = (vals, p) => { const s = [...vals].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(p * s.length))]; };

export function generateFrontline({ seed = 1, cols = FRONTLINE.cols, rows = FRONTLINE.rows } = {}) {
  if (rows % 2) throw new Error("rows must be even for a mirrored map");
  const rnd = mulberry32(seed);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const board = { cols, rows };
  const probe = { board, tiles: {}, units: [] };
  const cells = M.boardCells(probe);
  const Q = cols - 1 - (rows - 2) / 2;
  const mirror = (h) => ({ q: Q - h.q, r: rows - 1 - h.r });
  const half = rows / 2;
  const canonical = cells.filter((h) => h.r < half);
  const tiles = {};
  const relief = {};
  const set = (h, id) => { tiles[M.key(h)] = id; tiles[M.key(mirror(h))] = id; };
  const get = (h) => tiles[M.key(h)] ?? "plains";
  const inside = (h) => M.inBounds(probe, h);
  const ring = (c, k) => (k === 0 ? [c] : M.ringClockwise(c, k)).filter(inside);
  const disk = (c, k) => { const out = []; for (let i = 0; i <= k; i++) out.push(...ring(c, i)); return out; };

  // 1. Elevation + moisture on the canonical half, classified by percentile so every seed has the same mix.
  const elevN = valueNoise(seed * 7 + 1), moistN = valueNoise(seed * 13 + 5);
  const elev = new Map(), moist = new Map();
  for (const h of canonical) {
    const w = axialToWorld(h.q, h.r);
    const coast = Math.max(0, 2.5 - h.r) * 0.09; // northern coast; the mirror makes it a southern coast too
    elev.set(M.key(h), elevN(w.x * 0.075, w.z * 0.075) - coast);
    moist.set(M.key(h), moistN(w.x * 0.11 + 40, w.z * 0.11 + 40));
  }
  const ev = [...elev.values()];
  const tWater = quantile(ev, 0.1), tHill = quantile(ev, 0.8), tMount = quantile(ev, 0.93);
  const tForest = quantile([...moist.values()], 0.72);
  for (const h of canonical) {
    const e = elev.get(M.key(h)), m = moist.get(M.key(h));
    const id = e < tWater ? "water" : e > tMount ? "mountain" : e > tHill ? "hills" : m > tForest ? "forest" : "plains";
    set(h, id);
    const lift = id === "water" ? 0 : Math.max(0, Math.min(3, Math.floor(((e - tWater) / (tMount - tWater)) * 4)));
    relief[M.key(h)] = lift; relief[M.key(mirror(h))] = lift;
  }

  // 2. A river from the north coast to the centre line; its mirror runs from the south coast and meets it.
  const meet = { q: Math.ceil(Q / 2), r: half - 1 };
  {
    let q = meet.q + Math.floor(rnd() * Math.min(half - 1, 8));
    const river = [];
    for (let r = 0; r < half; r++) {
      river.push({ q, r });
      const left = q - meet.q, steps = half - 1 - r;
      if (steps <= 0) break;
      if (left >= steps) q -= 1;                 // must turn south-west to reach the meeting hex
      else if (left > 0 && rnd() < 0.5) q -= 1;  // otherwise meander
    }
    for (const h of river) if (inside(h)) set(h, "water");
    // Two fords per bank half, spaced out
    const crossable = river.filter((h) => h.r > 2 && h.r < half - 1);
    const fords = [];
    for (const h of crossable) if (fords.length < 2 && fords.every((f) => M.dist(f, h) > 4) && rnd() < 0.35) fords.push(h);
    if (!fords.length && crossable.length) fords.push(crossable[Math.floor(crossable.length / 2)]);
    for (const h of fords) set(h, "ford");
  }

  // 3. Command bases on the west/east centre line, with cleared ground around them.
  const gvsBase = { q: 3 - Math.floor((half - 1) / 2), r: half - 1 };
  const boeBase = mirror(gvsBase);
  for (const h of disk(gvsBase, 3)) { set(h, "plains"); relief[M.key(h)] = 1; relief[M.key(mirror(h))] = 1; }

  // 4. Main road base -> centre (mirrored, so it runs base to base and bridges the river).
  const road = [];
  {
    let cur = gvsBase;
    let guard = 0;
    while (!M.eq(cur, meet) && guard++ < 200) {
      const opts = M.DIRS.map((d) => M.add(cur, d)).filter((n) => inside(n) && M.dist(n, meet) < M.dist(cur, meet));
      cur = pick(opts);
      road.push(cur);
    }
    for (const h of road) set(h, "road");
  }

  // 5. Towns: small clusters of ruins beside the road.
  {
    const spots = road.filter((h) => M.dist(h, gvsBase) >= 6 && M.dist(h, meet) >= 3);
    const towns = [];
    for (const h of spots) if (towns.length < 2 && towns.every((t) => M.dist(t, h) > 6) && rnd() < 0.3) towns.push(h);
    for (const t of towns) {
      for (const n of ring(t, 1).filter((n) => get(n) !== "road" && get(n) !== "water").slice(0, 3)) set(n, "ruins");
    }
  }

  // 6. One mesa per side: a two-hex plateau with stairs from open ground.
  const links = [];
  {
    const cands = canonical.filter((h) => {
      if (M.dist(h, gvsBase) < 6 || M.dist(h, boeBase) < 6 || h.r < 2 || h.r > half - 3) return false;
      return ["plains", "hills", "forest"].includes(get(h));
    });
    for (let tries = 0; tries < 40 && cands.length; tries++) {
      const a = pick(cands);
      const b = M.add(a, M.DIRS[0]);
      const foot = ring(a, 1).find((n) => !M.eq(n, b) && ["plains", "road"].includes(get(n)) && n.r < half);
      if (!inside(b) || b.r >= half || !foot || !["plains", "hills", "forest"].includes(get(b))) continue;
      set(a, "plateau"); set(b, "plateau");
      links.push({ kind: "stairs", a: foot, b: a }, { kind: "stairs", a: mirror(foot), b: mirror(a) });
      break;
    }
  }

  // 7. Ore: one vein near each base for the harvester, then fields spread over ground reachable from the base.
  const passable = (h) => inside(h) && M.TERRAIN[get(h)].cost !== null && M.levelOf({ tiles }, h) === 0;
  const reach = new Set();
  {
    const queue = [gvsBase];
    reach.add(M.key(gvsBase));
    while (queue.length) {
      const c = queue.shift();
      for (const d of M.DIRS) {
        const n = M.add(c, d), k = M.key(n);
        if (!reach.has(k) && passable(n)) { reach.add(k); queue.push(n); }
      }
    }
  }
  const ore = [];
  {
    const near = ring(gvsBase, 3).filter((h) => h.r < half && reach.has(M.key(h)));
    const first = near.sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x)[0];
    if (first) ore.push(first);
    const fields = canonical.filter((h) => reach.has(M.key(h)) && ["plains", "forest", "hills"].includes(get(h)) &&
      M.dist(h, gvsBase) >= 4 && M.dist(h, boeBase) >= 4);
    for (let tries = 0; tries < 400 && ore.length < 7; tries++) {
      const h = pick(fields);
      if (ore.every((o) => M.dist(o, h) >= 5) && ore.every((o) => M.dist(mirror(o), h) >= 4)) ore.push(h);
    }
    for (const h of ore) set(h, "ore");
  }

  // 8. Armies: front rank of tanks and infantry, artillery behind, a harvester on the near vein.
  const units = [];
  let seq = 0;
  const unit = (type, owner, h) => {
    seq += 1;
    units.push({ id: `${owner === 1 ? "gvs" : "boe"}-${type}-${seq}`, type, owner, q: h.q, r: h.r,
      facing: owner === 1 ? 0 : 3, hp: M.UNITS[type].hp, moved: 0, attacked: false, fresh: false });
  };
  const ring2 = ring(gvsBase, 2).sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x);
  const roster = ["tank", "tank", "tank", "infantry", "infantry", "infantry", "infantry", "infantry", "infantry", "artillery", "artillery"];
  roster.forEach((type, i) => { unit(type, 1, ring2[i]); unit(type, 2, mirror(ring2[i])); });
  if (ore[0]) { unit("harvester", 1, ore[0]); unit("harvester", 2, mirror(ore[0])); }

  // 9. Two forward turrets per side on the home ring, covering the approaches.
  const structures = [];
  {
    const front = ring(gvsBase, 3)
      .filter((h) => !["ore", "road", "water", "mountain"].includes(get(h)) && !units.some((u) => M.eq(u, h)))
      .sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x);
    const picks = [];
    for (const h of front) if (picks.length < 2 && picks.every((p) => M.dist(p, h) >= 3)) picks.push(h);
    picks.forEach((h, i) => {
      structures.push({ id: `gvs-turret-${i + 1}`, type: "turret", owner: 1, q: h.q, r: h.r, hp: M.STRUCTURES.turret.hp });
      const m = mirror(h);
      structures.push({ id: `boe-turret-${i + 1}`, type: "turret", owner: 2, q: m.q, r: m.r, hp: M.STRUCTURES.turret.hp });
    });
  }

  return {
    board,
    structures,
    tiles,
    relief,
    links,
    map: { kind: "frontline", seed },
    domains: ["land", "air"],
    effects: [],
    firstPlayer: 1,
    rules: { apPerTurn: FRONTLINE.apPerTurn, maxTurns: FRONTLINE.maxTurns },
    bases: [
      { owner: 1, q: gvsBase.q, r: gvsBase.r, hp: M.RULES.baseHp, hpMax: M.RULES.baseHp },
      { owner: 2, q: boeBase.q, r: boeBase.r, hp: M.RULES.baseHp, hpMax: M.RULES.baseHp },
    ],
    players: { 1: { faction: "GVS", credits: FRONTLINE.credits, tech: 1 }, 2: { faction: "BOE", credits: FRONTLINE.credits, tech: 1 } },
    units,
    reserves: { 1: [], 2: [] },
    oreOwner: {},
    seq,
    timeExpired: false,
    turn: { player: 1, ap: FRONTLINE.apPerTurn, coordinatedAdvanceUsed: false, number: 1, round: 1 },
  };
}

export const ARCHIPELAGO = { cols: 36, rows: 24, apPerTurn: 12, maxTurns: 30, credits: 3200 };
export const HIGHLANDS = { cols: 36, rows: 24, apPerTurn: 12, maxTurns: 30, credits: 2800 };
export const GRAND = { cols: 60, rows: 40, apPerTurn: 14, maxTurns: 36, credits: 3200 };

function canvas(cols, rows, seed) {
  if (rows % 2) throw new Error("rows must be even for a mirrored map");
  const rnd = mulberry32(seed);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const board = { cols, rows };
  const probe = { board, tiles: {}, units: [] };
  const cells = M.boardCells(probe);
  const Q = cols - 1 - (rows - 2) / 2;
  const mirror = (h) => ({ q: Q - h.q, r: rows - 1 - h.r });
  const half = rows / 2;
  const canonical = cells.filter((h) => h.r < half);
  const tiles = {}, relief = {};
  const set = (h, id) => { tiles[M.key(h)] = id; tiles[M.key(mirror(h))] = id; };
  const get = (h) => tiles[M.key(h)] ?? "plains";
  const inside = (h) => M.inBounds(probe, h);
  const ring = (c, k) => (k === 0 ? [c] : M.ringClockwise(c, k)).filter(inside);
  const disk = (c, k) => { const out = []; for (let i = 0; i <= k; i++) out.push(...ring(c, i)); return out; };
  return { rnd, pick, board, probe, cells, Q, mirror, half, canonical, tiles, relief, set, get, inside, ring, disk };
}

function classifyHalf(ctx, seed, { water = 0.1, hill = 0.8, mount = 0.93, forest = 0.72, coast = 0 } = {}) {
  const { canonical, set, relief, tiles } = ctx;
  const elevN = valueNoise(seed * 7 + 1), moistN = valueNoise(seed * 13 + 5);
  const elev = new Map(), moist = new Map();
  for (const h of canonical) {
    const w = axialToWorld(h.q, h.r);
    const dip = coast ? Math.max(0, coast - h.r) * 0.09 : 0;
    elev.set(M.key(h), elevN(w.x * 0.075, w.z * 0.075) - dip);
    moist.set(M.key(h), moistN(w.x * 0.11 + 40, w.z * 0.11 + 40));
  }
  const ev = [...elev.values()];
  const tWater = quantile(ev, water), tHill = quantile(ev, hill), tMount = quantile(ev, mount);
  const tForest = quantile([...moist.values()], forest);
  for (const h of canonical) {
    const e = elev.get(M.key(h)), m = moist.get(M.key(h));
    const id = e < tWater ? "water" : e > tMount ? "mountain" : e > tHill ? "hills" : m > tForest ? "forest" : "plains";
    set(h, id);
    const lift = id === "water" ? 0 : Math.max(0, Math.min(3, Math.floor(((e - tWater) / Math.max(0.001, tMount - tWater)) * 4)));
    relief[M.key(h)] = lift; relief[M.key(ctx.mirror(h))] = lift;
  }
  return tiles;
}

function flood(ctx, start, pass) {
  const seen = new Set([M.key(start)]), queue = [start];
  while (queue.length) {
    const c = queue.shift();
    for (const d of M.DIRS) {
      const n = M.add(c, d), k = M.key(n);
      if (!seen.has(k) && ctx.inside(n) && pass(n)) { seen.add(k); queue.push(n); }
    }
  }
  return seen;
}

function carvePath(ctx, from, to, id = "water") {
  let cur = from, guard = 0;
  while (!M.eq(cur, to) && guard++ < 400) {
    const opts = M.DIRS.map((d) => M.add(cur, d)).filter((n) => ctx.inside(n) && M.dist(n, to) < M.dist(cur, to));
    if (!opts.length) break;
    cur = ctx.pick(opts);
    ctx.set(cur, id);
  }
}

function placeOre(ctx, base, reach, count = 6) {
  const { canonical, pick, set, get, mirror } = ctx;
  const ore = [];
  const near = ctx.ring(base, 3).filter((h) => h.r < ctx.half && reach.has(M.key(h)) && get(h) !== "water");
  if (near.length) ore.push(near.sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x)[0]);
  const fields = canonical.filter((h) => reach.has(M.key(h)) && ["plains", "forest", "hills"].includes(get(h)) &&
    M.dist(h, base) >= 4);
  for (let tries = 0; tries < 400 && ore.length < count; tries++) {
    const h = pick(fields);
    if (h && ore.every((o) => M.dist(o, h) >= 5) && ore.every((o) => M.dist(mirror(o), h) >= 4)) ore.push(h);
  }
  for (const h of ore) set(h, "ore");
  return ore;
}

function mintArmy(ctx, rosterLand, rosterSea, rosterAir, gvsBase, ore, dockWater) {
  const units = [];
  let seq = 0;
  const unit = (type, owner, h) => {
    seq += 1;
    units.push({
      id: `${owner === 1 ? "gvs" : "boe"}-${type}-${seq}`, type, owner, q: h.q, r: h.r,
      facing: owner === 1 ? 0 : 3, hp: M.UNITS[type].hp, moved: 0, attacked: false, fresh: false,
    });
  };
  const taken = new Set();
  const claim = (h) => { taken.add(M.key(h)); taken.add(M.key(ctx.mirror(h))); };
  const free = (list, pred) => list.find((h) => !taken.has(M.key(h)) && pred(h));
  const landRing = ctx.ring(gvsBase, 2).sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x);
  rosterLand.forEach((type) => {
    const h = free(landRing, (n) => ["plains", "road", "hills", "forest", "ore"].includes(ctx.get(n)));
    if (!h) return;
    unit(type, 1, h); unit(type, 2, ctx.mirror(h)); claim(h);
  });
  rosterAir.forEach((type) => {
    const h = free(landRing, (n) => ctx.get(n) !== "water" && ctx.get(n) !== "mountain");
    if (!h) return;
    unit(type, 1, h); unit(type, 2, ctx.mirror(h)); claim(h);
  });
  const seaSpots = (dockWater ?? []).concat(
    M.DIRS.map((d) => M.add(gvsBase, d)).filter((h) => ctx.inside(h) && ctx.get(h) === "water"),
    ctx.canonical.filter((h) => ctx.get(h) === "water" && M.dist(h, gvsBase) <= 5),
  );
  rosterSea.forEach((type) => {
    const h = free(seaSpots, (n) => ctx.get(n) === "water");
    if (!h) return;
    unit(type, 1, h); unit(type, 2, ctx.mirror(h)); claim(h);
  });
  if (ore[0] && rosterLand.includes("harvester")) {
    // already placed from roster
  } else if (ore[0] && !units.some((u) => u.type === "harvester")) {
    /* optional */
  }
  return { units, seq, taken };
}

function finishMap(st, kind, opts = {}) {
  const profile = PROFILES[kind];
  st.map = { kind, seed: opts.seed ?? st.map?.seed ?? 1 };
  st.domains = profile.domains.slice();
  st.effects = [...new Set([...(profile.effects ?? []), ...(opts.effects ?? [])])];
  st.firstPlayer = 1;
  if (st.effects.includes("fortified")) {
    for (const s of st.structures ?? []) s.hp = M.structureHp(st, s.type);
  }
  return st;
}

function matchShell(ctx, { credits, ap, maxTurns, tech = 1, gvsBase, units, seq, structures, links, seed, kind }) {
  return {
    board: ctx.board,
    structures,
    tiles: ctx.tiles,
    relief: ctx.relief,
    links: links ?? [],
    map: { kind, seed },
    domains: PROFILES[kind].domains.slice(),
    effects: [],
    firstPlayer: 1,
    rules: { apPerTurn: ap, maxTurns },
    bases: [
      { owner: 1, q: gvsBase.q, r: gvsBase.r, hp: M.RULES.baseHp, hpMax: M.RULES.baseHp },
      { owner: 2, q: ctx.mirror(gvsBase).q, r: ctx.mirror(gvsBase).r, hp: M.RULES.baseHp, hpMax: M.RULES.baseHp },
    ],
    players: { 1: { faction: "GVS", credits, tech }, 2: { faction: "BOE", credits, tech } },
    units,
    reserves: { 1: [], 2: [] },
    oreOwner: {},
    seq,
    timeExpired: false,
    turn: { player: 1, ap, coordinatedAdvanceUsed: false, number: 1, round: 1 },
  };
}

function placeDock(ctx, gvsBase, structures, taken) {
  const rim = [...ctx.disk(gvsBase, 3)].filter((h) => {
    if (M.eq(h, gvsBase) || taken.has(M.key(h))) return false;
    if (ctx.get(h) === "water" || ctx.get(h) === "mountain" || ctx.get(h) === "ore") return false;
    return M.DIRS.some((d) => ctx.inside(M.add(h, d)) && ctx.get(M.add(h, d)) === "water");
  }).sort((a, b) => M.dist(a, gvsBase) - M.dist(b, gvsBase));
  const h = rim[0];
  if (!h) return null;
  structures.push({ id: "gvs-dock-1", type: "dock", owner: 1, q: h.q, r: h.r, hp: M.STRUCTURES.dock.hp });
  const m = ctx.mirror(h);
  structures.push({ id: "boe-dock-1", type: "dock", owner: 2, q: m.q, r: m.r, hp: M.STRUCTURES.dock.hp });
  taken.add(M.key(h)); taken.add(M.key(m));
  return h;
}

function placeCoastal(ctx, gvsBase, structures, taken) {
  const rim = [...ctx.disk(gvsBase, 3)].filter((h) => {
    if (M.eq(h, gvsBase) || taken.has(M.key(h))) return false;
    if (ctx.get(h) === "water" || ctx.get(h) === "mountain" || ctx.get(h) === "ore") return false;
    return M.DIRS.some((d) => ctx.inside(M.add(h, d)) && ctx.get(M.add(h, d)) === "water");
  }).sort((a, b) => M.dist(a, gvsBase) - M.dist(b, gvsBase));
  const h = rim[0];
  if (!h) return null;
  structures.push({ id: "gvs-coastal-1", type: "coastal", owner: 1, q: h.q, r: h.r, hp: M.STRUCTURES.coastal.hp });
  const m = ctx.mirror(h);
  structures.push({ id: "boe-coastal-1", type: "coastal", owner: 2, q: m.q, r: m.r, hp: M.STRUCTURES.coastal.hp });
  taken.add(M.key(h)); taken.add(M.key(m));
  return h;
}

export function generateArchipelago({ seed = 1, effects } = {}) {
  const ctx = canvas(ARCHIPELAGO.cols, ARCHIPELAGO.rows, seed);
  classifyHalf(ctx, seed, { water: 0.6, hill: 0.88, mount: 0.96, forest: 0.78 });
  const gvsBase = { q: 4 - Math.floor((ctx.half - 1) / 2), r: ctx.half - 1 };
  for (const h of ctx.disk(gvsBase, 3)) {
    ctx.set(h, "plains");
    ctx.relief[M.key(h)] = 1;
    ctx.relief[M.key(ctx.mirror(h))] = 1;
  }
  // Guarantee a sea door on the island rim (mirrored).
  const door = ctx.ring(gvsBase, 3).find((h) => h.r < ctx.half) ?? ctx.ring(gvsBase, 2)[0];
  if (door) ctx.set(door, "water");
  const meet = { q: Math.ceil(ctx.Q / 2), r: ctx.half - 1 };
  carvePath(ctx, door ?? gvsBase, meet, "water");
  const port = M.DIRS.map((d) => M.add(gvsBase, d)).find((h) => ctx.inside(h) && ctx.get(h) === "water") ?? door;
  const sea = flood(ctx, port, (h) => ctx.get(h) === "water");
  const land = flood(ctx, gvsBase, (h) => ctx.get(h) !== "water" && ctx.get(h) !== "mountain" && M.TERRAIN[ctx.get(h)].cost !== null);
  const ore = placeOre(ctx, gvsBase, land, 5);
  const taken = new Set([M.key(gvsBase), M.key(ctx.mirror(gvsBase))]);
  const structures = [];
  const dock = placeDock(ctx, gvsBase, structures, taken);
  placeCoastal(ctx, gvsBase, structures, taken);
  const dockWater = dock ? M.DIRS.map((d) => M.add(dock, d)).filter((h) => ctx.inside(h) && ctx.get(h) === "water") : [];
  const { units, seq } = mintArmy(ctx, [], ["gunboat", "gunboat", "destroyer"], ["helicopter", "helicopter"], gvsBase, ore, dockWater);
  const st = matchShell(ctx, {
    credits: ARCHIPELAGO.credits, ap: ARCHIPELAGO.apPerTurn, maxTurns: ARCHIPELAGO.maxTurns,
    gvsBase, units, seq, structures, links: [], seed, kind: "archipelago",
  });
  return finishMap(st, "archipelago", { seed, effects });
}

export function generateHighlands({ seed = 1, effects } = {}) {
  const ctx = canvas(HIGHLANDS.cols, HIGHLANDS.rows, seed);
  classifyHalf(ctx, seed, { water: 0.04, hill: 0.55, mount: 0.78, forest: 0.62, coast: 1.2 });
  const gvsBase = { q: 3 - Math.floor((ctx.half - 1) / 2), r: ctx.half - 1 };
  const meet = { q: Math.ceil(ctx.Q / 2), r: ctx.half - 1 };
  for (const h of ctx.disk(gvsBase, 3)) {
    ctx.set(h, "plains");
    ctx.relief[M.key(h)] = 1;
    ctx.relief[M.key(ctx.mirror(h))] = 1;
  }
  let cur = gvsBase;
  for (let guard = 0; !M.eq(cur, meet) && guard < 200; guard++) {
    const opts = M.DIRS.map((d) => M.add(cur, d)).filter((n) => ctx.inside(n) && M.dist(n, meet) < M.dist(cur, meet));
    cur = ctx.pick(opts);
    ctx.set(cur, "road");
  }
  const land = flood(ctx, gvsBase, (h) => M.TERRAIN[ctx.get(h)].cost !== null && ctx.get(h) !== "water");
  const ore = placeOre(ctx, gvsBase, land, 6);
  const taken = new Set();
  const { units, seq } = mintArmy(ctx, ["tank", "tank", "infantry", "infantry", "infantry", "artillery", "harvester"], [], ["helicopter", "helicopter"], gvsBase, ore, []);
  if (ore[0] && !units.some((u) => u.type === "harvester" && u.owner === 1)) {
    /* harvester already in roster */
  }
  const structures = [];
  const front = ctx.ring(gvsBase, 3).filter((h) => !["ore", "water", "mountain"].includes(ctx.get(h)) && !units.some((u) => M.eq(u, h)))
    .sort((a, b) => axialToWorld(b.q, b.r).x - axialToWorld(a.q, a.r).x);
  const picks = [];
  for (const h of front) if (picks.length < 2 && picks.every((p) => M.dist(p, h) >= 3)) picks.push(h);
  picks.forEach((h, i) => {
    structures.push({ id: `gvs-turret-${i + 1}`, type: "turret", owner: 1, q: h.q, r: h.r, hp: M.STRUCTURES.turret.hp });
    const m = ctx.mirror(h);
    structures.push({ id: `boe-turret-${i + 1}`, type: "turret", owner: 2, q: m.q, r: m.r, hp: M.STRUCTURES.turret.hp });
  });
  const st = matchShell(ctx, {
    credits: HIGHLANDS.credits, ap: HIGHLANDS.apPerTurn, maxTurns: HIGHLANDS.maxTurns, tech: 2,
    gvsBase, units, seq, structures, links: [], seed, kind: "highlands",
  });
  return finishMap(st, "highlands", { seed, effects });
}

export function generateGrandTheater({ seed = 1, effects } = {}) {
  const ctx = canvas(GRAND.cols, GRAND.rows, seed);
  classifyHalf(ctx, seed, { water: 0.12, hill: 0.82, mount: 0.93, forest: 0.7, coast: 2.2 });
  // Ocean frame: north strip (mirrors to south) and both long edges, so a sea lane rings the continent.
  for (const h of ctx.canonical) {
    const col = h.q + Math.floor(h.r / 2);
    if (h.r < 3 || col <= 1 || col >= ctx.board.cols - 2) ctx.set(h, "water");
  }
  const gvsBase = { q: 7 - Math.floor((ctx.half - 1) / 2), r: ctx.half - 1 };
  for (const h of ctx.disk(gvsBase, 3)) {
    ctx.set(h, "plains");
    ctx.relief[M.key(h)] = 1;
    ctx.relief[M.key(ctx.mirror(h))] = 1;
  }
  // Inlet from the west ocean to the base disk so each HQ has a guaranteed port.
  const inlet = ctx.ring(gvsBase, 3).filter((h) => h.r < ctx.half)
    .sort((a, b) => (a.q + Math.floor(a.r / 2)) - (b.q + Math.floor(b.r / 2)))[0];
  if (inlet) ctx.set(inlet, "water");
  const ocean = ctx.canonical.find((h) => ctx.get(h) === "water" && h.r < 3);
  if (ocean && inlet) carvePath(ctx, inlet, ocean, "water");
  const meet = { q: Math.ceil(ctx.Q / 2), r: ctx.half - 1 };
  let cur = gvsBase;
  for (let guard = 0; !M.eq(cur, meet) && guard < 300; guard++) {
    const opts = M.DIRS.map((d) => M.add(cur, d)).filter((n) => ctx.inside(n) && ctx.get(n) !== "water" && M.dist(n, meet) < M.dist(cur, meet));
    if (!opts.length) break;
    cur = ctx.pick(opts);
    ctx.set(cur, "road");
  }
  const land = flood(ctx, gvsBase, (h) => M.TERRAIN[ctx.get(h)].cost !== null && ctx.get(h) !== "water");
  const ore = placeOre(ctx, gvsBase, land, 8);
  const taken = new Set();
  const structures = [];
  const dock = placeDock(ctx, gvsBase, structures, taken);
  placeCoastal(ctx, gvsBase, structures, taken);
  const dockWater = dock ? M.DIRS.map((d) => M.add(dock, d)).filter((h) => ctx.inside(h) && ctx.get(h) === "water") : [];
  const { units, seq } = mintArmy(
    ctx,
    ["tank", "tank", "infantry", "infantry", "infantry", "infantry", "artillery", "harvester"],
    ["gunboat", "gunboat", "destroyer"],
    ["helicopter"],
    gvsBase, ore, dockWater,
  );
  const front = ctx.ring(gvsBase, 3).filter((h) => !["ore", "water", "mountain"].includes(ctx.get(h)) && !units.some((u) => M.eq(u, h)));
  const picks = [];
  for (const h of front) if (picks.length < 2 && picks.every((p) => M.dist(p, h) >= 3)) picks.push(h);
  picks.forEach((h, i) => {
    structures.push({ id: `gvs-turret-${i + 1}`, type: "turret", owner: 1, q: h.q, r: h.r, hp: M.STRUCTURES.turret.hp });
    const m = ctx.mirror(h);
    structures.push({ id: `boe-turret-${i + 1}`, type: "turret", owner: 2, q: m.q, r: m.r, hp: M.STRUCTURES.turret.hp });
  });
  const st = matchShell(ctx, {
    credits: GRAND.credits, ap: GRAND.apPerTurn, maxTurns: GRAND.maxTurns,
    gvsBase, units, seq, structures, links: [], seed, kind: "grand-theater",
  });
  return finishMap(st, "grand-theater", { seed, effects });
}

export const PROFILES = {
  frontline: {
    label: "Frontline", detail: "36×24 land war · 12 AP · 30 rounds",
    cols: 36, rows: 24, domains: ["land", "air"], effects: [],
    generate: (opts) => finishMap(generateFrontline(opts), "frontline", opts),
  },
  archipelago: {
    label: "Archipelago", detail: "Island chain · ~60% water · rough seas",
    cols: 36, rows: 24, domains: ["sea", "air"], effects: ["rough-seas"],
    generate: generateArchipelago,
  },
  highlands: {
    label: "Highlands", detail: "Steep air war · both start at tech 2 · storm",
    cols: 36, rows: 24, domains: ["land", "air"], effects: ["storm"],
    generate: generateHighlands,
  },
  "grand-theater": {
    label: "Grand Theater", detail: "60×40 land, sea and air · sea lane",
    cols: 60, rows: 40, domains: ["land", "sea", "air"], effects: [],
    generate: generateGrandTheater,
  },
};

export function generateMap(kind, opts = {}) {
  const profile = PROFILES[kind];
  if (!profile) throw new Error(`unknown map ${kind}`);
  return profile.generate(opts);
}
